import { CheckCircle, ChevronLeft, ChevronRight, Draw, Person,
	RemoveCircle, ReportProblem } from "@mui/icons-material";
import { Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useAlert } from "../../../components/contextProviders/AlertProvider";
import EpiInfoBox from "../../../components/epi/EpiInfoBox";
import { EpiStatusChip } from "../../../components/epi/EpiStatusChip";
import EpiWizardActions from "../../../components/epi/EpiWizardActions";
import { EPI_COLORS, actionStatusColors, actionStatusLabels, complianceColors,
	reasonLabel } from "../../../components/epi/epiColors";
import { parseBrDate } from "../../epi-inspection-detail/EpiInspectionDetail";
import { EpiInspectionFlowService } from "../../../services/EpiInspectionFlowService";
import { StepProps } from "../EpiInspectionWizard";
import { IMMEDIATE_ACTIONS } from "./EpiChecklistStep";

export const DECLARATION_VERSION = '2.0';

const ACTION_DUE_DAYS = 7;

export const NR06_DECLARATIONS = [
	'Tomei ciência das condições registradas nesta inspeção.',
	'Recebi orientação sobre o uso correto dos EPIs.',
	'Fui orientado sobre guarda, conservação e higienização.',
	'Fui informado sobre a necessidade de comunicar dano, extravio ou inutilização.',
	'Tomei ciência das não conformidades registradas (quando aplicável).'
];

const EpiSummaryStep: FC<StepProps> = ({ inspection, onSave }) => {
	const [accepted, setAccepted] = useState<string[]>([]);
	const [observation, setObservation] = useState(inspection.generalObservation || '');
	const { showError } = useAlert();
	const service = new EpiInspectionFlowService();
	const nonCompliant = inspection.items.filter(item => item.status === 'NAO_OK');
	const okCount = inspection.items.filter(item => item.status === 'OK').length;
	const notApplicableCount = inspection.items.filter(item => item.status === 'NAO_APLICAVEL').length;
	const result = nonCompliant.length ? 'NAO_CONFORME' : 'CONFORME';
	const resultColor = complianceColors[result];

	function toggle(declaration: string, checked: boolean) {
		setAccepted(previous => checked
			? [...previous, declaration]
			: previous.filter(current => current !== declaration));
	}

	return (
		<div className="passo passo-resumo">
			<div className="principal">
				<Typography variant="subtitle1">Resumo da inspeção</Typography>
				<div className="painel resultado">
					<div className="bloco-resultado">
						<span className="icone-resultado" style={{ backgroundColor: resultColor }}>
							<ReportProblem fontSize="large" />
						</span>
						<div>
							<span className="rotulo">RESULTADO DA INSPEÇÃO</span>
							<Typography variant="h5" style={{ color: resultColor }}>
								{result === 'NAO_CONFORME' ? 'NÃO CONFORME' : 'CONFORME'}
							</Typography>
							<span className="detalhe">
								{nonCompliant.length === 1
									? '1 não conformidade identificada'
									: `${nonCompliant.length} não conformidades identificadas`}
							</span>
						</div>
					</div>
					<div className="divisoria-vertical" />
					<div className="contadores">
						<div className="contador-resultado">
							<CheckCircle style={{ color: EPI_COLORS.ok }} />
							<Typography variant="h5">{okCount}</Typography>
							<span>OK</span>
						</div>
						<div className="contador-resultado">
							<ReportProblem style={{ color: EPI_COLORS.danger }} />
							<Typography variant="h5">{nonCompliant.length}</Typography>
							<span>NÃO OK</span>
						</div>
						<div className="contador-resultado">
							<RemoveCircle style={{ color: EPI_COLORS.neutral }} />
							<Typography variant="h5">{notApplicableCount}</Typography>
							<span>NÃO APLICÁVEL</span>
						</div>
					</div>
				</div>
				<div className="painel">
					<Typography variant="subtitle1">Equipamentos de Proteção Individual</Typography>
					<div className="tabela tabela-resumo">
						<div className="cabecalho-tabela">
							<span>EPI RECOMENDADO PARA A FUNÇÃO</span>
							<span>STATUS</span>
							<span>DETALHAMENTO</span>
						</div>
						{inspection.items.map((item, index) => (
							<div className="linha" key={item.id || index}>
								<span>{item.epiName}</span>
								<span style={{ color: statusColor(item.status) }}>{statusLabel(item.status)}</span>
								<span style={item.status === 'NAO_OK' ? { color: EPI_COLORS.danger } : undefined}>
									{detailOf(item.status, item.observation, item.nonConformityReason, item.nonConformityReasonDetail)}
								</span>
							</div>
						))}
					</div>
				</div>
				<div className="painel">
					<div className="titulo-painel">
						<Typography variant="subtitle1">Ciência do trabalhador</Typography>
						<span>Leia atentamente as declarações abaixo e confirme sua ciência.</span>
					</div>
					<div className="declaracoes">
						{NR06_DECLARATIONS.map(declaration => (
							<FormControlLabel
								key={declaration}
								label={declaration}
								control={
									<Checkbox
										checked={accepted.includes(declaration)}
										onChange={(e) => toggle(declaration, e.target.checked)}
									/>
								}
							/>
						))}
					</div>
					<EpiInfoBox variant="info" title="Importante">
						<p>Esta ciência não exime a organização de suas responsabilidades previstas na NR-06.</p>
					</EpiInfoBox>
				</div>
				<EpiWizardActions
					leftLabel="VOLTAR"
					leftIcon={<ChevronLeft />}
					onLeft={() => onSave(0, inspection.items, observation)}
					draftLabel="SALVAR RASCUNHO"
					onDraft={() => onSave(1, inspection.items, observation)}
					primaryLabel="CONTINUAR"
					primaryIcon={<ChevronRight />}
					onPrimary={confirm}
				/>
			</div>
			<div className="lateral">
				{!!nonCompliant.length && (
					<div className="painel">
						<Typography variant="subtitle1">Não conformidade identificada</Typography>
						{nonCompliant.map((item, index) => (
							<div className="card-nc" key={item.id || index}>
								<Typography variant="subtitle2" style={{ color: EPI_COLORS.danger }}>
									{item.epiName}
								</Typography>
								<p>
									<strong>Problema: </strong>
									{reasonLabel(item.nonConformityReason, item.nonConformityReasonDetail)}
								</p>
								<p>
									<strong>Ação imediata: </strong>
									{IMMEDIATE_ACTIONS[item.immediateAction || ''] || item.immediateAction || '—'}
								</p>
								<p><strong>Responsável: </strong>{inspection.responsibleName || '—'}</p>
								<p><strong>Prazo da ação: </strong>{actionDueDate()}</p>
								<p>
									<strong>Status: </strong>
									<EpiStatusChip label={actionStatusLabels.ABERTA} color={actionStatusColors.ABERTA} />
								</p>
							</div>
						))}
					</div>
				)}
				<div className="painel">
					<Typography variant="subtitle1">Observação geral</Typography>
					<TextField
						size="small"
						multiline
						minRows={4}
						value={observation}
						onChange={(e) => setObservation(e.target.value)}
					/>
				</div>
				<div className="painel">
					<Typography variant="subtitle1">Próximos passos</Typography>
					<div className="etapa">
						<span className="icone-etapa" style={{ borderColor: EPI_COLORS.success,
							color: EPI_COLORS.success }}>
							<CheckCircle fontSize="small" />
						</span>
						<div>
							<strong>Inspeção de EPIs concluída</strong>
							<span>{inspection.inspectionDate || '—'}</span>
						</div>
					</div>
					<div className="seta">↓</div>
					<div className="etapa">
						<span className="icone-etapa" style={{ borderColor: EPI_COLORS.warning,
							color: EPI_COLORS.warning }}>
							<Person fontSize="small" />
						</span>
						<div>
							<strong>Ciência do trabalhador</strong>
							<span>Em andamento</span>
						</div>
					</div>
					<div className="seta">↓</div>
					<div className="etapa">
						<span className="icone-etapa" style={{ borderColor: EPI_COLORS.neutral,
							color: EPI_COLORS.neutral }}>
							<Draw fontSize="small" />
						</span>
						<div>
							<strong>Coleta de assinatura</strong>
							<span>Próxima etapa</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	function statusLabel(status?: string) {
		if (status === 'OK') {
			return 'OK';
		}
		if (status === 'NAO_OK') {
			return 'NÃO OK';
		}
		return 'NÃO APLICÁVEL';
	}

	function statusColor(status?: string) {
		if (status === 'OK') {
			return EPI_COLORS.ok;
		}
		if (status === 'NAO_OK') {
			return EPI_COLORS.danger;
		}
		return EPI_COLORS.neutral;
	}

	function detailOf(status?: string, observation?: string, reason?: string, reasonDetail?: string) {
		if (observation) {
			return observation;
		}
		if (status === 'NAO_OK') {
			return reasonLabel(reason, reasonDetail);
		}
		if (status === 'NAO_APLICAVEL') {
			return 'Não se aplica à atividade realizada no momento.';
		}
		return 'Utilizado corretamente.';
	}

	function actionDueDate() {
		const datePart = (inspection.inspectionDate || '').split(' ')[0];
		const time = parseBrDate(datePart);
		if (time === null || Number.isNaN(time)) {
			return '—';
		}
		const due = new Date(time);
		due.setDate(due.getDate() + ACTION_DUE_DAYS);
		const day = String(due.getDate()).padStart(2, '0');
		const month = String(due.getMonth() + 1).padStart(2, '0');
		return `${day}/${month}/${due.getFullYear()}`;
	}

	async function confirm() {
		try {
			const acceptedDeclarations = NR06_DECLARATIONS.filter(declaration => accepted.includes(declaration));
			if (acceptedDeclarations.length) {
				await service.acknowledge(inspection.id, acceptedDeclarations.join('\n'), DECLARATION_VERSION);
			}
			await onSave(2, inspection.items, observation);
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível registrar a ciência.');
		}
	}
};

export default EpiSummaryStep;
